import { Component, OnInit, ViewEncapsulation } from '@angular/core'
import { UserReviewClient } from 'src/app/shared/services/ppv.api'

export interface CycleConfigEntry {
   key: string
   isEnabled: boolean
   openDate: string
   closeDate: string
}

const TEAM_CHILD_KEYS = ['CLINICAL', 'CROSSTRAINED', 'RECEPTION']

@Component({
   selector: 'app-cycles',
   templateUrl: './cycles.component.html',
   styleUrls: ['./cycles.component.scss'],
   standalone: false,
   encapsulation: ViewEncapsulation.None,
})
export class CyclesComponent implements OnInit {
   readonly CYCLE_KEYS = ['DVM_Q1', 'DVM_Q2', 'DVM_Q3', 'DVM_Q4', 'HM', 'COS', 'RM', 'DO', 'OPEN', 'CLINICAL', 'CROSSTRAINED', 'RECEPTION'] as const

   cycleConfig: Record<string, CycleConfigEntry> = this.buildDefaultCycleConfig()
   private cycleConfigSnapshot: Record<string, CycleConfigEntry> = this.buildDefaultCycleConfig()

   cycleGroups: { key: string; label: string; sub: string; icon: string; isChild: boolean; isTeamChild?: boolean; isLast?: boolean }[] = [
      { key: 'DVM_Q1', label: 'Q1', sub: '', icon: '', isChild: true },
      { key: 'DVM_Q2', label: 'Q2', sub: '', icon: '', isChild: true },
      { key: 'DVM_Q3', label: 'Q3', sub: '', icon: '', isChild: true },
      { key: 'DVM_Q4', label: 'Q4', sub: '', icon: '', isChild: true, isLast: true },
      { key: 'HM', label: 'HM', sub: 'Hospital Manager', icon: 'fa-hospital-user', isChild: false },
      { key: 'COS', label: 'COS', sub: 'Chief of Staff', icon: 'fa-user-group', isChild: false },
      { key: 'RM', label: 'RM', sub: 'Regional Manager', icon: 'fa-user-alt', isChild: false },
      { key: 'DO', label: 'DO', sub: 'Director of Operations', icon: 'fa-user-tie', isChild: false },
      { key: 'OPEN', label: 'OPEN', sub: 'Mixed Reviews', icon: 'fa-folder-open', isChild: false },
      { key: 'CLINICAL', label: 'Clinical', sub: '', icon: '', isChild: false, isTeamChild: true },
      { key: 'CROSSTRAINED', label: 'Cross-Trained', sub: '', icon: '', isChild: false, isTeamChild: true },
      { key: 'RECEPTION', label: 'Reception', sub: '', icon: '', isChild: false, isTeamChild: true, isLast: true },
   ]

   cyclesLoading = false
   cyclesSaving = false
   cyclesSaveSuccess: string | null = null
   cyclesSaveError: string | null = null

   constructor(private userReviewClient: UserReviewClient) {}

   ngOnInit(): void {
      this.loadCycleConfig()
   }

   private buildDefaultCycleConfig(): Record<string, CycleConfigEntry> {
      const result: Record<string, CycleConfigEntry> = {}
      for (const key of this.CYCLE_KEYS) {
         result[key] = { key, isEnabled: false, openDate: '', closeDate: '' }
      }
      return result
   }

   loadCycleConfig(): void {
      this.cyclesLoading = true
      this.userReviewClient.getCycleConfig().subscribe({
         next: (dtos) => {
            dtos.forEach((dto) => {
               if (this.cycleConfig[dto.key]) {
                  this.cycleConfig[dto.key] = {
                     key: dto.key,
                     isEnabled: dto.isEnabled,
                     openDate: this.dateToString(dto.openDate),
                     closeDate: this.dateToString(dto.closeDate),
                  }
               }
            })
            this.cycleConfigSnapshot = this.cloneCycleConfig()
            this.cyclesLoading = false
         },
         error: () => {
            this.cyclesLoading = false
         },
      })
   }

   toggleCycle(key: string): void {
      if (this.cycleConfig[key]) this.cycleConfig[key].isEnabled = !this.cycleConfig[key].isEnabled
   }

   getAdvmActiveCount(): number {
      return ['DVM_Q1', 'DVM_Q2', 'DVM_Q3', 'DVM_Q4'].filter((k) => this.cycleConfig[k]?.isEnabled).length
   }

   getTeamActiveCount(): number {
      return TEAM_CHILD_KEYS.filter((k) => this.cycleConfig[k]?.isEnabled).length
   }

   discardCycleChanges(): void {
      this.cycleConfig = this.cloneCycleConfig(this.cycleConfigSnapshot)
      this.cyclesSaveSuccess = null
      this.cyclesSaveError = null
   }

   saveCycleConfig(): void {
      this.cyclesSaving = true
      this.cyclesSaveSuccess = null
      this.cyclesSaveError = null

      const payload = Object.values(this.cycleConfig).map((entry) => ({
         key: entry.key,
         isEnabled: entry.isEnabled,
         openDate: this.stringToDate(entry.openDate),
         closeDate: this.stringToDate(entry.closeDate),
      }))

      this.userReviewClient.saveCycleConfig(payload).subscribe({
         next: () => {
            this.cycleConfigSnapshot = this.cloneCycleConfig()
            this.cyclesSaving = false
            this.cyclesSaveSuccess = 'Cycle configuration saved.'
            setTimeout(() => (this.cyclesSaveSuccess = null), 3000)
         },
         error: () => {
            this.cyclesSaving = false
            this.cyclesSaveError = 'Failed to save. Please try again.'
            setTimeout(() => (this.cyclesSaveError = null), 4000)
         },
      })
   }

   private dateToString(date: Date | null | undefined): string {
      if (!date) return ''
      const d = new Date(date)
      if (isNaN(d.getTime())) return ''
      return d.toISOString().substring(0, 10)
   }

   private stringToDate(value: string | null | undefined): Date | null {
      if (!value) return null
      const d = new Date(value)
      return isNaN(d.getTime()) ? null : d
   }

   private cloneCycleConfig(source?: Record<string, CycleConfigEntry>): Record<string, CycleConfigEntry> {
      const src = source ?? this.cycleConfig
      const clone: Record<string, CycleConfigEntry> = {}
      for (const key of Object.keys(src)) clone[key] = { ...src[key] }
      return clone
   }
}
